import { ApiResult } from "common/models";
import { HttpClient } from "common/services/http-client.service";
import { ExternalApiClient } from "common/services/external-api-client.service";
import { withApiResponse } from "common/utils/rtk-query.util";
import { configurationHubConfig } from "../config/configuration-hub.config";
import {
  AssignmentItem,
  AssignmentListResponse,
  CreateAssignmentRequest,
  UpdateAssignmentRequest,
  ListParams,
  DocumentType,
  DocumentTypeListResponse,
  ConfigurationOperation,
  DatabaseConfiguration,
  DatabaseConfigurationListResponse,
  ExtractionConfigurationItem,
  ExtractionConfigurationListResponse,
  CreateExtractionConfigurationRequest,
  UpdateExtractionConfigurationRequest,
  ApiToolResponse,
  Tool,
  TagLibraryApiResponse,
  TagLibraryByIdResponse,
  TagLibraryConfig
} from "../models/configuration-hub.model";

export class ConfigurationHubService extends HttpClient {
  // Assignment services
  public static getAllAssignments(params: ListParams) {
    const config = configurationHubConfig.getAllAssignments(params);
    return withApiResponse<AssignmentListResponse>(async () => {
      const response = await this.invokeApi<AssignmentListResponse>(config);
      return new ApiResult<AssignmentListResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static createAssignment(assignment: CreateAssignmentRequest) {
    const config = configurationHubConfig.createAssignment(assignment);
    return withApiResponse<AssignmentItem>(async () => {
      const response = await this.invokeApi<AssignmentItem>(config);
      return new ApiResult<AssignmentItem>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static updateAssignment(updateRequest: UpdateAssignmentRequest) {
    const config = configurationHubConfig.updateAssignment(updateRequest);
    return withApiResponse<AssignmentItem>(async () => {
      const response = await this.invokeApi<AssignmentItem>(config);
      return new ApiResult<AssignmentItem>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static async deleteAssignment(assignmentId: number) {
    const config = configurationHubConfig.deleteAssignment(assignmentId);
    const response = await this.invokeApi<void>(config);
    return response;
  }

  public static getAssignmentById(assignmentId: number) {
    const config = configurationHubConfig.getAssignmentById(assignmentId);
    return withApiResponse<AssignmentItem>(async () => {
      const response = await this.invokeApi<AssignmentItem>(config);
      return new ApiResult<AssignmentItem>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  // Document Type services
  public static getAllDocumentTypes() {
    const config = configurationHubConfig.getAllDocumentTypes();
    return withApiResponse<DocumentTypeListResponse>(async () => {
      const response = await this.invokeApi<DocumentTypeListResponse>(config);
      return new ApiResult<DocumentTypeListResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static createDocumentType(documentType: Partial<DocumentType>) {
    const config = configurationHubConfig.createDocumentType(documentType);
    return withApiResponse<DocumentType>(async () => {
      const response = await this.invokeApi<DocumentType>(config);
      return new ApiResult<DocumentType>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  // Configuration services
  public static getConfigurationOperations(assignmentId?: number) {
    const config =
      configurationHubConfig.getConfigurationOperations(assignmentId);
    return withApiResponse<ConfigurationOperation[]>(async () => {
      const response = await this.invokeApi<ConfigurationOperation[]>(config);
      return new ApiResult<ConfigurationOperation[]>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static saveConfiguration(configuration: ConfigurationOperation) {
    const config = configurationHubConfig.saveConfiguration(configuration);
    return withApiResponse<ConfigurationOperation>(async () => {
      const response = await this.invokeApi<ConfigurationOperation>(config);
      return new ApiResult<ConfigurationOperation>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  // Database Configuration services
  public static getAllDatabaseConfigurations() {
    const config = configurationHubConfig.getAllDatabaseConfigurations();
    return withApiResponse<DatabaseConfiguration[]>(async () => {
      // Use ExternalApiClient for external API calls
      const response = await ExternalApiClient.get<DatabaseConfiguration[]>(
        config.url
      );
      return new ApiResult<DatabaseConfiguration[]>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  // Extraction Configuration services
  public static getAllExtractionConfigurations(params: ListParams) {
    const config =
      configurationHubConfig.getAllExtractionConfigurations(params);
    return withApiResponse<ExtractionConfigurationListResponse>(async () => {
      const response =
        await this.invokeApi<ExtractionConfigurationListResponse>(config);
      return new ApiResult<ExtractionConfigurationListResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static createExtractionConfiguration(
    configuration: CreateExtractionConfigurationRequest
  ) {
    const config =
      configurationHubConfig.createExtractionConfiguration(configuration);
    return withApiResponse<ExtractionConfigurationItem>(async () => {
      const response = await this.invokeApi<ExtractionConfigurationItem>(
        config
      );
      return new ApiResult<ExtractionConfigurationItem>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static updateExtractionConfiguration(
    updateRequest: UpdateExtractionConfigurationRequest
  ) {
    const config =
      configurationHubConfig.updateExtractionConfiguration(updateRequest);
    return withApiResponse<ExtractionConfigurationItem>(async () => {
      const response = await this.invokeApi<ExtractionConfigurationItem>(
        config
      );
      return new ApiResult<ExtractionConfigurationItem>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static async deleteExtractionConfiguration(configurationId: number) {
    const config =
      configurationHubConfig.deleteExtractionConfiguration(configurationId);
    const response = await this.invokeApi<void>(config);
    return response;
  }

  public static getExtractionConfigurationById(configurationId: number) {
    const config =
      configurationHubConfig.getExtractionConfigurationById(configurationId);
    return withApiResponse<ExtractionConfigurationItem>(async () => {
      const response = await this.invokeApi<ExtractionConfigurationItem>(
        config
      );
      return new ApiResult<ExtractionConfigurationItem>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  // Tag Library Configuration services
  public static getAllTagLibraryConfigurations() {
    const config = configurationHubConfig.getAllTagLibraryConfigurations();
    return withApiResponse<TagLibraryApiResponse>(async () => {
      // Use ExternalApiClient for external API calls
      const response = await ExternalApiClient.get<TagLibraryApiResponse>(
        config.url
      );
      return new ApiResult<TagLibraryApiResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static async deleteTagLibraryConfiguration(guidCode: string) {
    const config =
      configurationHubConfig.deleteTagLibraryConfiguration(guidCode);
    return withApiResponse<void>(async () => {
      // Use ExternalApiClient DELETE method for external API calls
      const response = await ExternalApiClient.delete<void>(config.url);
      return new ApiResult<void>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static updateTagConfigCardUpdate(
    guidCode: string,
    updateData: { name: string; description: string; configurationName: string }
  ) {
    return withApiResponse<void>(async () => {
      const response = await ExternalApiClient.put<void>(
        `${process.env.REACT_APP_ENG_API_URL}/api/Documents/tagConfigCardUpdate/${guidCode}`,
        updateData
      );
      return new ApiResult<void>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getTagConfigCardUpdate(guidCode: string) {
    const config = configurationHubConfig.getTagConfigCardUpdate(guidCode);
    return withApiResponse<TagLibraryConfig>(async () => {
      // Use ExternalApiClient for external API calls
      const response = await ExternalApiClient.put<TagLibraryConfig>(
        config.url,
        {} // Empty body since we're just fetching config details
      );
      return new ApiResult<TagLibraryConfig>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  // Tools services
  public static async getAllTools(): Promise<ApiResult<Tool[]>> {
    try {
      const response = await ExternalApiClient.get<ApiToolResponse[]>(
        `${process.env.REACT_APP_ENG_API_URL}/api/Documents/tools`
      );

      if (response.statusCode === 200 && response.value) {
        // Map API response to Tool interface
        const tools: Tool[] = response.value.map((apiTool) => {
          let inputFields: string[] = [];
          try {
            // Parse the fields JSON string
            inputFields = JSON.parse(apiTool.fields);
          } catch (e) {
            console.warn("Failed to parse fields for tool:", apiTool.name, e);
            inputFields = [];
          }

          return {
            id: apiTool.guidCode,
            name: apiTool.name
              .toLowerCase()
              .replace(/[^a-z0-9\s]/g, "")
              .replace(/\s+/g, "_"),
            displayName: apiTool.name,
            description: apiTool.description,
            isCustom: false,
            functionName:
              apiTool.name
                .toLowerCase()
                .replace(/[^a-z0-9\s]/g, "")
                .replace(/\s+/g, "_") + "_tool",
            inputFields: inputFields,
            status: apiTool.approved ? "approved" : "pending"
          };
        });

        return new ApiResult<Tool[]>(tools, 200);
      } else {
        return new ApiResult<Tool[]>(
          [],
          response.statusCode,
          "Failed to fetch tools"
        );
      }
    } catch (error: any) {
      console.error("Error fetching tools:", error);
      return new ApiResult<Tool[]>(
        [],
        500,
        error.message || "Failed to fetch tools"
      );
    }
  }

  // Tag Library Upload service
  public static uploadTagLibrary(
    file: File,
    categoryName: string,
    configurationName?: string,
    description?: string
  ) {
    return withApiResponse<any>(async () => {
      // Create FormData for multipart/form-data request
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category_name", categoryName);

      if (configurationName) {
        formData.append("configuration_name", configurationName);
      }

      if (description) {
        formData.append("description", description);
      }

      const config = configurationHubConfig.uploadTagLibrary(formData);
      const response = await this.invokeApi<any>(config);

      return new ApiResult<any>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  // Tag Enrichment service
  public static getTagEnrichmentDetails(
    CFIHOSMasterObjectId: number,
    isActive = true
  ) {
    const config = configurationHubConfig.getTagEnrichmentDetails(
      CFIHOSMasterObjectId,
      isActive
    );

    return withApiResponse<any>(async () => {
      const response = await ExternalApiClient.get<any>(config.url);
      return new ApiResult<any>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static enrichTagDefinitions(guidCodes: string[]) {
    const config = configurationHubConfig.enrichTagDefinitions(guidCodes);

    return withApiResponse<any>(async () => {
      const response = await ExternalApiClient.post<any>(
        config.url,
        config.data
      );
      return new ApiResult<any>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static deleteTagEnrichment(guidCode: string) {
    return withApiResponse<void>(async () => {
      const response = await ExternalApiClient.delete<void>(
        `${process.env.REACT_APP_ENG_API_URL}/api/Documents/tagEnrichmentDelete/${guidCode}`
      );
      return new ApiResult<void>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static updateTagEnrichment(guidCode: string, tagDefinition: string) {
    return withApiResponse<void>(async () => {
      const response = await ExternalApiClient.put<void>(
        `${process.env.REACT_APP_ENG_API_URL}/api/Documents/tagEnrichmentUpdate/${guidCode}`,
        { description: tagDefinition }
      );
      return new ApiResult<void>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static processEmbedding(data: { masterObjectId: number }) {
    return withApiResponse<any>(async () => {
      const response = await ExternalApiClient.post<any>(
        `${process.env.REACT_APP_ENG_API_URL}/api/tagLibrary/processEmbedding`,
        data
      );
      return new ApiResult<any>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getTagLibraryById(id: number) {
    return withApiResponse<TagLibraryByIdResponse>(async () => {
      const response = await ExternalApiClient.get<TagLibraryByIdResponse>(
        `${process.env.REACT_APP_ENG_API_URL}/api/tagLibrary/${id}`
      );
      return new ApiResult<TagLibraryByIdResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }
}
